import { processingRealtimeStore } from '../services/processingRealtimeStore';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  FileDown,
  FolderInput,
  Lock,
  Play,
  RefreshCw,
  Wifi,
  WifiOff,
} from 'lucide-react';
import {
  apiService,
  getApiErrorMessage,
  ImportJob,
  MetadataExport,
  ProcessingStreamEvent,
  PromptRun,
} from '../services/api';
import { useFeatureAccess } from '../contexts/FeatureAccessContext';
import { usePassiveApiActivity } from '../hooks/usePassiveApiActivity';
import { isInProgressStatus } from '../utils/realtimeStreamEligibility';
import { retrySafeRead } from '../services/safeRead';
import { toPromptRunFromProcessingEvent } from '../utils/promptRunEventMapper';
import { promptRunHasSelectionFilter } from '../features/workflows/config';
import {
  getPromptRunFallbackSummary,
  getPromptRunIndexId,
} from '../utils/promptRunProgress';
import PromptRunProgressCardCompact from './PromptRunProgressCardCompact';
import ImportJobCardCompact from './ImportJobCardCompact';
import ExportJobCardCompact from './ExportJobCardCompact';
import LockedFeaturePanel from './LockedFeaturePanel';
import { parseArtifactLink } from '../navigation/resultLinks';
import WorkflowArtifactPanel from './WorkflowArtifactPanel';
import './JobsTab.css';

type JobTabType = 'prompt_runs' | 'import_jobs' | 'export_jobs';

const HISTORY_LIMIT = 200;


const parseTimestamp = (value?: string | null): number => {
  if (!value) return 0;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatDateTime = (value?: string | null): string => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const sortPromptRuns = (runs: PromptRun[]): PromptRun[] => {
  return [...runs]
    .sort((left, right) => parseTimestamp(right.executed_at) - parseTimestamp(left.executed_at))
    .slice(0, HISTORY_LIMIT);
};

const sortImportJobs = (jobs: ImportJob[]): ImportJob[] => {
  return [...jobs]
    .sort((left, right) => parseTimestamp(right.created_at) - parseTimestamp(left.created_at))
    .slice(0, HISTORY_LIMIT);
};

const sortExports = (exports: MetadataExport[]): MetadataExport[] => {
  return [...exports]
    .sort((left, right) => parseTimestamp(right.created_at) - parseTimestamp(left.created_at))
    .slice(0, HISTORY_LIMIT);
};

const upsertPromptRun = (runs: PromptRun[], incoming: PromptRun): PromptRun[] => {
  const byId = new Map(runs.map((run) => [run.run_id, run]));
  const existing = byId.get(incoming.run_id);
  byId.set(incoming.run_id, {
    ...existing,
    ...incoming,
    run_context: incoming.run_context || existing?.run_context || {},
  });
  return sortPromptRuns(Array.from(byId.values()));
};

const upsertImportJob = (jobs: ImportJob[], incoming: ImportJob): ImportJob[] => {
  const byId = new Map(jobs.map((job) => [job.job_id, job]));
  const existing = byId.get(incoming.job_id);
  byId.set(incoming.job_id, {
    ...existing,
    ...incoming,
    progress: incoming.progress || existing?.progress || {
      total_files: 0,
      imported: 0,
      failed: 0,
      skipped: 0,
      bytes_transferred: 0,
    },
    video_ids: incoming.video_ids || existing?.video_ids || [],
    failed_files: incoming.failed_files || existing?.failed_files || [],
    failed_files_next_cursor: incoming.failed_files_next_cursor ?? existing?.failed_files_next_cursor,
  });
  return sortImportJobs(Array.from(byId.values()));
};

const upsertMetadataExport = (exports: MetadataExport[], incoming: MetadataExport): MetadataExport[] => {
  const byId = new Map(exports.map((job) => [job.export_id, job]));
  const existing = byId.get(incoming.export_id);
  byId.set(incoming.export_id, {
    ...existing,
    ...incoming,
  });
  return sortExports(Array.from(byId.values()));
};

const toImportJobFromEvent = (
  event: ProcessingStreamEvent,
  existing?: ImportJob
): ImportJob | null => {
  const payload = event.payload?.snapshot || event.payload || {};
  const jobId = String(payload.job_id || existing?.job_id || '').trim();
  if (!jobId) return null;

  const statusFallback = event.event_type.includes('cancelled')
    ? 'cancelled'
    : event.event_type.includes('failed')
      ? 'failed'
      : event.event_type.includes('completed')
        ? 'completed'
        : event.event_type === 'import_job.scanning'
          ? 'scanning'
          : event.event_type === 'import_job.started'
            ? 'importing'
            : 'pending';

  const progressFromPayload = payload.progress && typeof payload.progress === 'object'
    ? payload.progress
    : {};

  const progress = {
    bytes_total: progressFromPayload.bytes_total ?? existing?.progress?.bytes_total,
    bytes_confirmed: progressFromPayload.bytes_confirmed ?? existing?.progress?.bytes_confirmed,
    parts_total: Number(progressFromPayload.parts_total ?? existing?.progress?.parts_total ?? 0),
    parts_completed: Number(progressFromPayload.parts_completed ?? existing?.progress?.parts_completed ?? 0),
    parts_failed: Number(progressFromPayload.parts_failed ?? existing?.progress?.parts_failed ?? 0),
    total_files: Number(progressFromPayload.total_files ?? existing?.progress?.total_files ?? 0),
    imported: Number(progressFromPayload.imported ?? existing?.progress?.imported ?? 0),
    failed: Number(progressFromPayload.failed ?? existing?.progress?.failed ?? 0),
    skipped: Number(progressFromPayload.skipped ?? existing?.progress?.skipped ?? 0),
    bytes_transferred: Number(progressFromPayload.bytes_transferred ?? existing?.progress?.bytes_transferred ?? 0),
    current_file: (progressFromPayload.current_file as string) ?? existing?.progress?.current_file,
  };

  return {
    ...(existing || {
      job_id: jobId,
      connector_id: '',
      target_index_id: '',
      source_prefix: '',
      file_pattern: '*',
      recursive: true,
      status: 'pending',
      progress,
      video_ids: [],
      failed_files: [],
    }),
    ...payload,
    job_id: jobId,
    phase: (payload.phase as string) || existing?.phase,
    connector_id: String(payload.connector_id || existing?.connector_id || ''),
    target_index_id: String(payload.target_index_id || existing?.target_index_id || ''),
    source_prefix: String(payload.source_prefix || existing?.source_prefix || ''),
    file_pattern: String(payload.file_pattern || existing?.file_pattern || '*'),
    recursive: Boolean(payload.recursive ?? existing?.recursive ?? true),
    status: String(payload.status || existing?.status || statusFallback) as ImportJob['status'],
    created_at: String(payload.created_at || existing?.created_at || event.created_at || new Date().toISOString()),
    started_at: (payload.started_at as string) || existing?.started_at,
    completed_at: (payload.completed_at as string) || existing?.completed_at,
    error_message: (payload.error_message as string) || existing?.error_message,
    progress,
  };
};

const toExportFromEvent = (
  event: ProcessingStreamEvent,
  existing?: MetadataExport
): MetadataExport | null => {
  const payload = event.payload || {};
  const exportId = String(payload.export_id || existing?.export_id || '').trim();
  if (!exportId) return null;
  const exportParams = (
    payload.export_params
    && typeof payload.export_params === 'object'
    && !Array.isArray(payload.export_params)
  )
    ? payload.export_params as Record<string, unknown>
    : {};
  const destinationConnectorId = String(
    payload.destination_connector_id
      || exportParams.destination_connector_id
      || existing?.destination_connector_id
      || ''
  ).trim() || undefined;
  const rawDestinationType = String(
    payload.destination_type
      || existing?.destination_type
      || (destinationConnectorId ? 'connector' : '')
  ).trim().toLowerCase();
  const destinationType = (
    rawDestinationType === 'download' || rawDestinationType === 'connector'
  )
    ? rawDestinationType as MetadataExport['destination_type']
    : undefined;

  const statusFallback = event.event_type === 'export.completed'
    ? 'completed'
    : event.event_type === 'export.failed'
      ? 'failed'
      : 'processing';

  return {
    ...(existing || {
      export_id: exportId,
      export_type: 'index',
      target_id: '',
      created_at: event.created_at || new Date().toISOString(),
      status: 'processing',
    }),
    export_id: exportId,
    export_type: String(payload.export_type || existing?.export_type || 'index') as MetadataExport['export_type'],
    target_id: String(payload.target_id || existing?.target_id || ''),
    created_at: String(payload.created_at || existing?.created_at || event.created_at || new Date().toISOString()),
    status: String(payload.status || existing?.status || statusFallback) as MetadataExport['status'],
    queue_status: String(payload.queue_status || existing?.queue_status || ''),
    attempts: Number(payload.attempts ?? existing?.attempts ?? 0),
    max_attempts: Number(payload.max_attempts ?? existing?.max_attempts ?? 1),
    available_at: (payload.available_at as string) || existing?.available_at,
    started_at: (payload.started_at as string) || existing?.started_at,
    completed_at: (payload.completed_at as string) || existing?.completed_at,
    updated_at: (payload.updated_at as string) || existing?.updated_at,
    download_url: (payload.download_url as string) || existing?.download_url,
    file_size_bytes: Number(payload.file_size_bytes ?? existing?.file_size_bytes ?? 0) || undefined,
    error_message: (payload.error_message as string) || existing?.error_message,
    last_error: (payload.last_error as string) || existing?.last_error,
    gcs_uri: (payload.gcs_uri as string) || existing?.gcs_uri,
    export_params: (payload.export_params as Record<string, any>) || existing?.export_params,
    destination_type: destinationConnectorId ? 'connector' : destinationType,
    destination_connector_id: destinationConnectorId,
    destination_base_path: (payload.destination_base_path as string) || existing?.destination_base_path,
    destination_subpath: (payload.destination_subpath as string) || existing?.destination_subpath,
    destination_uri: (payload.destination_uri as string) || existing?.destination_uri,
  };
};


const JobsTab: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const artifactTarget = useMemo(() => parseArtifactLink(location.search), [location.search]);
  const artifactTargetKey = artifactTarget ? JSON.stringify({ ...artifactTarget, download: false }) : '';
  const consumeArtifactDownload = () => {
    const params = new URLSearchParams(location.search);
    params.delete('action');
    navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
  };
  const { getLockedFeature } = useFeatureAccess();
  const exportJobsLockedFeature = getLockedFeature('export_lifecycle');
  const canLoadImportJobs = true;
  const canLoadExportJobs = !exportJobsLockedFeature;
  const [activeTab, setActiveTab] = useState<JobTabType>(new URLSearchParams(location.search).get('tab') === 'exports' ? 'export_jobs' : 'prompt_runs');
  useEffect(() => {
    const requestedTab = new URLSearchParams(location.search).get('tab');
    if (requestedTab === 'exports') setActiveTab('export_jobs');
    else if (requestedTab === 'imports') setActiveTab('import_jobs');
    else if (requestedTab === 'runs') setActiveTab('prompt_runs');
  }, [location.search]);
  const [promptRuns, setPromptRuns] = useState<PromptRun[]>([]);
  const [importJobs, setImportJobs] = useState<ImportJob[]>([]);
  const [exportJobs, setExportJobs] = useState<MetadataExport[]>([]);

  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [historyErrors, setHistoryErrors] = useState<Partial<Record<JobTabType, string>>>({});

  const canObserve = usePassiveApiActivity(
    promptRuns.some((run) => isInProgressStatus(run.status))
    || importJobs.some((job) => isInProgressStatus(job.status))
    || exportJobs.some((job) => isInProgressStatus(job.status)),
  );

  const [streamConnected, setStreamConnected] = useState(false);
  const [streamError, setStreamError] = useState<string | null>(null);
  const [lastStreamActivityAt, setLastStreamActivityAt] = useState<string | null>(null);

  // Track expanded state for each job type
  const [expandedPromptRuns, setExpandedPromptRuns] = useState<Set<string>>(new Set());
  const [expandedImportJobs, setExpandedImportJobs] = useState<Set<string>>(new Set());
  const [expandedExportJobs, setExpandedExportJobs] = useState<Set<string>>(new Set());
  const progressScopeKey = JSON.stringify({
    run_ids: promptRuns.filter((run) => isInProgressStatus(run.status)).map((run) => run.run_id).slice(0, 100),
    import_job_ids: importJobs.filter((job) => isInProgressStatus(job.status)).map((job) => job.job_id).slice(0, 100),
    export_ids: canLoadExportJobs ? exportJobs.filter((job) => isInProgressStatus(job.status)).map((job) => job.export_id).slice(0, 100) : [],
  });

  const togglePromptRunExpanded = useCallback((runId: string) => {
    setExpandedPromptRuns((prev) => {
      const next = new Set(prev);
      if (next.has(runId)) {
        next.delete(runId);
      } else {
        next.add(runId);
      }
      return next;
    });
  }, []);

  const toggleImportJobExpanded = useCallback((jobId: string) => {
    setExpandedImportJobs((prev) => {
      const next = new Set(prev);
      if (next.has(jobId)) {
        next.delete(jobId);
      } else {
        next.add(jobId);
      }
      return next;
    });
  }, []);

  const toggleExportJobExpanded = useCallback((exportId: string) => {
    setExpandedExportJobs((prev) => {
      const next = new Set(prev);
      if (next.has(exportId)) {
        next.delete(exportId);
      } else {
        next.add(exportId);
      }
      return next;
    });
  }, []);

  const loadJobs = useCallback(async (silent: boolean = false) => {
    if (silent) {
      setIsRefreshing(true);
    } else {
      setIsLoading(true);
    }
    setError(null);
    setHistoryErrors({});

    try {
      const [runs, imports, exports] = await Promise.allSettled([
        retrySafeRead(() => apiService.getPromptRuns(HISTORY_LIMIT)),
        canLoadImportJobs ? retrySafeRead(() => apiService.getImportJobs(undefined, undefined, HISTORY_LIMIT)) : Promise.resolve([]),
        canLoadExportJobs ? retrySafeRead(() => apiService.getExports(HISTORY_LIMIT)) : Promise.resolve([]),
      ]);
      const failed: Partial<Record<JobTabType, string>> = {};
      if (runs.status === 'fulfilled') setPromptRuns(sortPromptRuns(runs.value));
      else failed.prompt_runs = getApiErrorMessage(runs.reason, 'Workflow executions could not be loaded. Refresh to try again.');
      if (imports.status === 'fulfilled') setImportJobs(sortImportJobs(imports.value));
      else failed.import_jobs = getApiErrorMessage(imports.reason, 'Import jobs could not be loaded. Refresh to try again.');
      if (exports.status === 'fulfilled') setExportJobs(sortExports(exports.value));
      else failed.export_jobs = getApiErrorMessage(exports.reason, 'Export jobs could not be loaded. Refresh to try again.');
      setHistoryErrors(failed);
    } finally {
      if (silent) {
        setIsRefreshing(false);
      } else {
        setIsLoading(false);
      }
    }
  }, [canLoadExportJobs, canLoadImportJobs]);

  const handleStreamEvent = useCallback((event: ProcessingStreamEvent) => {
    setLastStreamActivityAt(event.created_at || new Date().toISOString());
    setStreamConnected(true);
    setStreamError(null);

    if (event.event_type.startsWith('run.')) {
      const runId = String(event.payload?.run_id || event.run_id || '').trim();
      setPromptRuns((current) => {
        const existing = runId ? current.find((item) => item.run_id === runId) : undefined;
        const patched = toPromptRunFromProcessingEvent(event, existing);
        if (!patched) return current;
        return upsertPromptRun(current, patched);
      });
      return;
    }

    if (event.event_type.startsWith('import_job.')) {
      if (!canLoadImportJobs) return;
      const jobId = String(event.payload?.job_id || '').trim();
      setImportJobs((current) => {
        const existing = jobId ? current.find((item) => item.job_id === jobId) : undefined;
        const patched = toImportJobFromEvent(event, existing);
        if (!patched) return current;
        return upsertImportJob(current, patched);
      });
      return;
    }

    if (event.event_type.startsWith('export.')) {
      if (!canLoadExportJobs) return;
      const exportId = String(event.payload?.export_id || '').trim();
      setExportJobs((current) => {
        const existing = exportId ? current.find((item) => item.export_id === exportId) : undefined;
        const patched = toExportFromEvent(event, existing);
        if (!patched) return current;
        return upsertMetadataExport(current, patched);
      });
    }
  }, [canLoadExportJobs, canLoadImportJobs]);

  useEffect(() => { if (canObserve) void loadJobs(false); }, [canObserve, loadJobs]);

  useEffect(() => {
    if (!canObserve) { setStreamConnected(false); return; }
    const unsubscribe = processingRealtimeStore.subscribe(handleStreamEvent, JSON.parse(progressScopeKey), (state) => {
      setStreamConnected(state === 'connected' || state === 'idle');
      setStreamError(state === 'unavailable' ? 'Live updates are unavailable. Use Refresh to reconnect.'
        : state === 'reconnecting' ? 'Reconnecting live updates…' : null);
    });
    return unsubscribe;
  }, [canObserve, handleStreamEvent, progressScopeKey]);

  const renderTabContent = () => {
    const historyError = historyErrors[activeTab];
    const errorNotice = historyError && <div className="jobs-info-banner error" role="alert">{historyError}</div>;
    if (activeTab === 'prompt_runs') {
      return (
        <div className="jobs-tab-content">
          {errorNotice}
          {promptRuns.length === 0 && !historyError ? (
            <div className="jobs-empty">No workflow executions yet.</div>
          ) : (
            promptRuns.map((run) => (
              <PromptRunProgressCardCompact
                key={run.run_id}
                run={run}
                summary={getPromptRunFallbackSummary(run)}
                isExpanded={expandedPromptRuns.has(run.run_id)}
                onToggleExpand={togglePromptRunExpanded}
                onOpenWorkflow={(selectedRun) => {
                  const indexId = getPromptRunIndexId(selectedRun);
                  if (!indexId) return;
                  const params = new URLSearchParams({ index: indexId, run: selectedRun.run_id });
                  if (selectedRun.prompt_id) params.set('prompt', selectedRun.prompt_id);
                  navigate(`/workflows?${params.toString()}`);
                }}
                onOpenResults={(selectedRun) => {
                  const indexId = getPromptRunIndexId(selectedRun);
                  if (!indexId) return;
                  const selectionStatus = selectedRun.selection_summary?.status;
                  const result = promptRunHasSelectionFilter(selectedRun) && selectionStatus === 'ready'
                    ? 'matches'
                    : 'all';
                  const params = new URLSearchParams({
                    index: indexId,
                    run: selectedRun.run_id,
                    result,
                  });
                  navigate(`/index?${params.toString()}`);
                }}
              />
            ))
          )}
        </div>
      );
    }

    if (activeTab === 'import_jobs') {
      return (
        <div className="jobs-tab-content">
          {errorNotice}
          {importJobs.length === 0 && !historyError ? (
            <div className="jobs-empty">No import jobs yet.</div>
          ) : (
            importJobs.map((job) => (
              <ImportJobCardCompact
                key={job.job_id}
                job={job}
                isExpanded={expandedImportJobs.has(job.job_id)}
                onToggleExpand={toggleImportJobExpanded}
              />
            ))
          )}
        </div>
      );
    }

    if (artifactTarget) {
      return <><button type="button" onClick={() => navigate('/jobs?tab=exports')}>View export history</button>
      <WorkflowArtifactPanel key={artifactTargetKey} target={artifactTarget} onDownloadConsumed={consumeArtifactDownload} onExportResolved={(exportId) => {
        const params = new URLSearchParams(location.search);
        params.set('export', exportId);
        navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
      }} /></>;
    }

    if (exportJobsLockedFeature) {
      return <LockedFeaturePanel feature={exportJobsLockedFeature} />;
    }

    return (
      <div className="jobs-tab-content">
        {errorNotice}
        {exportJobs.length === 0 && !historyError ? (
          <div className="jobs-empty">No export jobs yet.</div>
        ) : (
          exportJobs.map((job) => (
            <ExportJobCardCompact
              key={job.export_id}
              job={job}
              isExpanded={expandedExportJobs.has(job.export_id)}
              onToggleExpand={toggleExportJobExpanded}
            />
          ))
        )}
      </div>
    );
  };

  return (
    <div className="jobs-tab">
      <div className="jobs-header">
        <div>
          <h2>Jobs</h2>
          <p>Monitor workflow executions, imports, exports, usage, and operational status.</p>
        </div>
        <div className="jobs-header-actions">
          <div className={`jobs-stream-status ${streamConnected ? 'connected' : 'disconnected'}`}>
            {streamConnected ? <Wifi size={14} /> : <WifiOff size={14} />}
            <span>{streamConnected ? 'Live' : 'Reconnecting'}</span>
          </div>
          <button
            className="jobs-refresh-btn"
            onClick={() => {
              void loadJobs(true);
              processingRealtimeStore.refresh();
            }}
            disabled={isRefreshing || isLoading}
          >
            <RefreshCw size={14} className={isRefreshing ? 'spinning' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {streamError && <div className="jobs-info-banner warning">{streamError}</div>}
      {error && <div className="jobs-info-banner error">{error}</div>}
      {lastStreamActivityAt && (
        <div className="jobs-last-activity">Last event: {formatDateTime(lastStreamActivityAt)}</div>
      )}

      {isLoading && !artifactTarget ? (
        <div className="jobs-loading-state">
          <RefreshCw size={20} className="spinning" />
          <span>Loading jobs...</span>
        </div>
      ) : (
        <div className="jobs-tabs-container">
          <div className="jobs-tabs">
            <button
              className={`jobs-tab-btn ${activeTab === 'prompt_runs' ? 'active' : ''}`}
              onClick={() => setActiveTab('prompt_runs')}
            >
              <Play size={14} />
              <span>Workflow Executions</span>
              <span className="tab-count">{promptRuns.length}</span>
            </button>
            <button
              className={`jobs-tab-btn ${activeTab === 'import_jobs' ? 'active' : ''}`}
              onClick={() => setActiveTab('import_jobs')}
            >
              <FolderInput size={14} />
              <span>Import Jobs</span>
              <span className="tab-count">{importJobs.length}</span>
            </button>
            <button
              className={`jobs-tab-btn ${activeTab === 'export_jobs' ? 'active' : ''} ${exportJobsLockedFeature ? 'locked' : ''}`}
              onClick={() => setActiveTab('export_jobs')}
              title={exportJobsLockedFeature?.reason}
            >
              {exportJobsLockedFeature ? <Lock size={14} /> : <FileDown size={14} />}
              <span>Export Jobs</span>
              <span className="tab-count">{exportJobs.length}</span>
            </button>
          </div>

          {renderTabContent()}
        </div>
      )}
    </div>
  );
};

export default JobsTab;
